import React, { memo, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled, { css, keyframes } from "styled-components";

const pulse = keyframes`
  0%, 100% { opacity: 0.38; transform: scale(1); }
  50% { opacity: 0.9; transform: scale(1.08); }
`;

const voiceLevel = keyframes`
  0%, 100% { transform: scaleY(0.58); opacity: 0.5; }
  45% { transform: scaleY(1); opacity: 0.92; }
  70% { transform: scaleY(0.74); opacity: 0.68; }
`;

const riseBars = keyframes`
  0%, 100% { transform: scaleY(0.55); }
  50% { transform: scaleY(1); }
`;

const gaugeSwing = keyframes`
  0%, 100% { transform: rotate(-30deg); }
  50% { transform: rotate(4deg); }
`;

const coreBreathe = keyframes`
  0%, 100% { box-shadow: 0 0 0 rgba(var(--accent-rgb), 0), inset 0 1px 0 rgba(255, 255, 255, 0.08); }
  50% { box-shadow: 0 0 34px rgba(var(--accent-rgb), 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1); }
`;

const drawLine = keyframes`
  0% { stroke-dashoffset: 150; opacity: 0.4; }
  45%, 70% { stroke-dashoffset: 0; opacity: 1; }
  100% { stroke-dashoffset: 0; opacity: 0.66; }
`;

const orbitLoop = keyframes`
  to { stroke-dashoffset: -100; }
`;

const flowBeam = keyframes`
  to { stroke-dashoffset: -92; }
`;

function AboutWorkVisual({ variant, aspect = "4 / 3", className, title, ...frameProps }) {
  const [frameRef, motionActive] = useViewportMotion();

  return (
    <Frame
      {...frameProps}
      ref={frameRef}
      className={className}
      $aspect={aspect}
      $motionActive={motionActive}
      role="img"
      aria-label={title || ""}
      data-motion-state={motionActive ? "active" : "paused"}
    >
      <Backdrop />
      <Grid />
      <Glow />
      {variant === "agents" ? <AgentsVisual /> : null}
      {variant === "integrations" ? <IntegrationsVisual /> : null}
      {variant === "dashboards" ? <DashboardsVisual /> : null}
      <Overlay />
    </Frame>
  );
}

function useViewportMotion() {
  const ref = useRef(null);
  const [motionActive, setMotionActive] = useState(false);

  useEffect(() => {
    const node = ref.current;

    if (!node) {
      return undefined;
    }

    if (typeof window === "undefined" || typeof window.IntersectionObserver !== "function") {
      setMotionActive(true);
      return undefined;
    }

    const observer = new window.IntersectionObserver(
      ([entry]) => {
        setMotionActive(entry.isIntersecting || entry.intersectionRatio > 0);
      },
      {
        rootMargin: "180px 0px",
        threshold: 0.01,
      }
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  return [ref, motionActive];
}

function AgentsVisual() {
  const phoneToAi = "M 33 50 C 38 49 42 49 47 50";

  return (
    <AgentsScene>
      <Connections viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
        <ConnectionPath d={phoneToAi} />
        <FlowPath className="agent-flow-beam flow-beam" d={phoneToAi} $delay="0s" />
        <FlowStreak className="agent-flow-streak flow-streak" d={phoneToAi} $delay="0.24s" />
      </Connections>
      <Phone>
        <PhoneTop />
        <PhoneBody>
          <ChatBubble $tone="muted" $w="68%" />
          <VoiceCallCard className="voice-call-card" aria-hidden="true">
            <CallIconBadge className="call-icon-badge">
              <CallIcon viewBox="0 0 36 36">
                <path d="M12.4 8.2c-1.5.7-2.7 2.5-2.8 4.2-.3 5 6.7 13.1 12.2 14.2 1.8.4 3.9-.4 4.9-1.7l1.5-2-5-3.3-1.6 1.4c-.7.6-1.7.7-2.5.2-1.9-1-4.2-3.5-5-5.4-.3-.8-.1-1.7.5-2.3l1.4-1.4-3.6-3.9Z" />
              </CallIcon>
            </CallIconBadge>
            <CallWaveStack>
              <CallWave className="voice-wave" $delay="0s" />
              <CallWave className="voice-wave" $delay="0.16s" />
              <CallWave className="voice-wave" $delay="0.32s" />
              <CallWave className="voice-wave" $delay="0.48s" />
            </CallWaveStack>
          </VoiceCallCard>
          <ChatBubble $tone="muted" $w="66%" />
          <ChatBubble $tone="bright" $w="48%" />
        </PhoneBody>
      </Phone>
      <OpsPanel>
        <OpsTop>
          <OpsMark>AI</OpsMark>
          <OpsBars>
            <OpsBar />
            <OpsBar />
            <OpsBar />
          </OpsBars>
        </OpsTop>
        <OpsCard>
          <OpsCardHeader />
          <OpsRows>
            <OpsRow $w="84%" />
            <OpsRow $w="72%" />
            <OpsRow $w="78%" />
          </OpsRows>
        </OpsCard>
        <OpsFooter>
          <MetricCard>
            <MetricLine />
            <MetricWave />
          </MetricCard>
          <MetricCard>
            <MetricLine />
            <VoiceMeter aria-hidden="true">
              <VoiceBar className="voice-wave" $delay="0s" />
              <VoiceBar className="voice-wave" $delay="0.12s" />
              <VoiceBar className="voice-wave" $delay="0.24s" />
              <VoiceBar className="voice-wave" $delay="0.36s" />
              <VoiceBar className="voice-wave" $delay="0.48s" />
            </VoiceMeter>
          </MetricCard>
        </OpsFooter>
      </OpsPanel>
    </AgentsScene>
  );
}

function IntegrationsVisual() {
  const { i18n } = useTranslation();
  const language = i18n?.language || "nl";
  const isNl = language.startsWith("nl");
  const labels = {
    webshop: "WEBSHOP",
    webshopAria: isNl ? "Webshop order" : "Webshop order",
    crm: "CRM",
    crmAria: isNl ? "CRM klantprofiel" : "CRM customer profile",
    apiAria: isNl ? "API sync kern" : "API sync core",
    accounting: isNl ? "BOEKHOUDING" : "ACCOUNTING",
    accountingAria: isNl ? "Boekhouding factuur" : "Accounting invoice",
    dashboard: "DASHBOARD",
    dashboardAria: isNl ? "Dashboard inzichten" : "Dashboard insights",
  };
  const shopToCore = "M 33 27 C 41 29 44 38 47 45";
  const crmToCore = "M 33 72 C 40 68 44 60 47 54";
  const coreToAccounting = "M 58 45 C 63 37 68 30 76 27";
  const coreToDashboard = "M 58 55 C 64 62 69 69 76 73";

  return (
    <IntegrationScene>
      <Connections viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
        <ConnectionPath d={shopToCore} />
        <ConnectionPath d={crmToCore} />
        <ConnectionPath d={coreToAccounting} />
        <ConnectionPath d={coreToDashboard} />
        <FlowPath className="flow-beam" d={shopToCore} $delay="0s" />
        <FlowPath className="flow-beam" d={crmToCore} $delay="0.7s" />
        <FlowPath className="flow-beam" d={coreToAccounting} $delay="1.1s" />
        <FlowPath className="flow-beam" d={coreToDashboard} $delay="1.5s" />
        <FlowStreak className="flow-streak" d={shopToCore} $delay="0.2s" />
        <FlowStreak className="flow-streak" d={crmToCore} $delay="0.9s" />
        <FlowStreak className="flow-streak" d={coreToAccounting} $delay="1.3s" />
        <FlowStreak className="flow-streak" d={coreToDashboard} $delay="1.7s" />
      </Connections>

      <SystemNode
        aria-label={labels.webshopAria}
        data-integration-node
        $top="13%"
        $left="7%"
        $w="28%"
        $h="28%"
        $delay="0s"
      >
        <SystemChrome>
          <StatusLight />
          <SystemLabel>{labels.webshop}</SystemLabel>
        </SystemChrome>
        <ShopMini />
      </SystemNode>

      <SystemNode
        aria-label={labels.crmAria}
        data-integration-node
        $top="59%"
        $left="7%"
        $w="28%"
        $h="28%"
        $delay="0.18s"
      >
        <SystemChrome>
          <StatusLight />
          <SystemLabel>{labels.crm}</SystemLabel>
        </SystemChrome>
        <CrmMini />
      </SystemNode>

      <SyncCore aria-label={labels.apiAria} $delay="0.08s">
        <CoreOrbit />
        <CoreLabel>API</CoreLabel>
        <CorePill>SYNC</CorePill>
        <CoreRows>
          <CoreRow />
          <CoreRow />
          <CoreRow />
        </CoreRows>
      </SyncCore>

      <SystemNode
        aria-label={labels.accountingAria}
        data-integration-node
        $top="13%"
        $left="67%"
        $w="26%"
        $h="28%"
        $delay="0.32s"
      >
        <SystemChrome>
          <StatusLight />
          <SystemLabel $tight>{labels.accounting}</SystemLabel>
        </SystemChrome>
        <AccountingMini />
      </SystemNode>

      <SystemNode
        aria-label={labels.dashboardAria}
        data-integration-node
        $top="59%"
        $left="67%"
        $w="26%"
        $h="28%"
        $delay="0.44s"
      >
        <SystemChrome>
          <StatusLight />
          <SystemLabel>{labels.dashboard}</SystemLabel>
        </SystemChrome>
        <DashboardMini />
      </SystemNode>
    </IntegrationScene>
  );
}

function ShopMini() {
  return (
    <MiniSvg viewBox="0 0 132 86" aria-hidden="true">
      <rect className="mini-line" x="18" y="4" width="78" height="6" rx="3" />
      <g className="shop-products">
        <rect className="mini-panel" x="8" y="22" width="34" height="44" rx="7" />
        <rect className="mini-panel" x="49" y="18" width="34" height="48" rx="7" />
        <rect className="mini-panel" x="90" y="22" width="34" height="44" rx="7" />
        <rect className="shop-price" x="16" y="72" width="18" height="5" rx="2.5" />
        <rect className="shop-price" x="57" y="72" width="18" height="5" rx="2.5" />
        <rect className="shop-price" x="98" y="72" width="18" height="5" rx="2.5" />
      </g>
    </MiniSvg>
  );
}

function CrmMini() {
  return (
    <MiniCrm>
      <MiniAvatar />
      <MiniCrmRows>
        <MiniRow $w="84%" />
        <MiniRow $w="62%" />
        <MiniRow $w="72%" />
      </MiniCrmRows>
    </MiniCrm>
  );
}

function AccountingMini() {
  return (
    <MiniSvg viewBox="0 0 120 78" aria-hidden="true">
      <rect className="mini-line" x="10" y="12" width="78" height="6" rx="3" />
      <rect className="mini-line" x="10" y="28" width="96" height="6" rx="3" />
      <rect className="mini-line" x="10" y="44" width="68" height="6" rx="3" />
      <path className="invoice-check" d="M88 60l8 8 16-22" />
    </MiniSvg>
  );
}

function DashboardMini() {
  return (
    <MiniSvg viewBox="0 0 120 78" aria-hidden="true">
      <path className="mini-area" d="M8 58 L23 51 L38 54 L56 40 L74 45 L93 31 L112 22 L112 70 L8 70 Z" />
      <path className="mini-spark-base" d="M8 58 L23 51 L38 54 L56 40 L74 45 L93 31 L112 22" />
      <path className="mini-spark" d="M8 58 L23 51 L38 54 L56 40 L74 45 L93 31 L112 22" />
    </MiniSvg>
  );
}

function CoreOrbit() {
  return (
    <CoreOrbitSvg viewBox="0 0 120 120" aria-hidden="true">
      <rect className="core-track" x="14" y="14" width="92" height="92" rx="30" pathLength="100" />
      <rect className="core-runner" x="14" y="14" width="92" height="92" rx="30" pathLength="100" />
    </CoreOrbitSvg>
  );
}

const KPI_TRENDS = [
  {
    line: "M6 54 C18 56 28 52 42 49 C58 45 68 34 84 37 C101 41 113 26 132 22 C143 20 150 16 154 12",
    area: "M6 54 C18 56 28 52 42 49 C58 45 68 34 84 37 C101 41 113 26 132 22 C143 20 150 16 154 12 L154 68 L6 68 Z",
  },
  {
    line: "M6 58 C20 56 31 60 45 51 C58 43 72 47 86 39 C99 31 114 28 130 23 C142 19 149 19 154 16",
    area: "M6 58 C20 56 31 60 45 51 C58 43 72 47 86 39 C99 31 114 28 130 23 C142 19 149 19 154 16 L154 68 L6 68 Z",
  },
  {
    line: "M6 56 C18 54 29 57 42 50 C55 43 66 45 80 41 C94 37 104 27 120 24 C136 21 146 17 154 14",
    area: "M6 56 C18 54 29 57 42 50 C55 43 66 45 80 41 C94 37 104 27 120 24 C136 21 146 17 154 14 L154 68 L6 68 Z",
  },
];

const MAIN_CHART = {
  line: "M0 92 C30 90 48 86 74 80 C106 72 126 58 154 62 C188 68 205 46 236 40 C267 35 288 26 320 18",
  area: "M0 92 C30 90 48 86 74 80 C106 72 126 58 154 62 C188 68 205 46 236 40 C267 35 288 26 320 18 L320 120 L0 120 Z",
};

function KpiTrendCard({ trend }) {
  return (
    <KpiCard data-dashboard-kpi>
      <KpiHeader>
        <KpiLabel />
        <KpiPulseBars aria-hidden="true">
          <span />
          <span />
          <span />
        </KpiPulseBars>
      </KpiHeader>
      <KpiValueRow>
        <KpiValue />
        <KpiValueGhost />
      </KpiValueRow>
      <KpiSparkSvg
        viewBox="0 0 160 70"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <path className="kpi-spark-area" d={trend.area} />
        <path className="kpi-spark-base" d={trend.line} pathLength="150" />
        <path className="kpi-spark-static" d={trend.line} pathLength="150" />
      </KpiSparkSvg>
    </KpiCard>
  );
}

function MainDashboardChart() {
  return (
    <ChartArea data-dashboard-main-chart>
      <MainChartSvg viewBox="0 0 320 120" preserveAspectRatio="none" aria-hidden="true">
        <path className="dashboard-main-area" d={MAIN_CHART.area} />
        <path className="dashboard-main-base" d={MAIN_CHART.line} pathLength="150" />
        <path className="dashboard-main-spark" d={MAIN_CHART.line} pathLength="150" />
      </MainChartSvg>
    </ChartArea>
  );
}

function DashboardsVisual() {
  return (
    <DashboardScene>
      <DashboardTop>
        {KPI_TRENDS.map((trend, index) => (
          <KpiTrendCard key={index} trend={trend} />
        ))}
      </DashboardTop>
      <MainDashboardChart />
      <LowerGrid>
        <BarsPanel>
          <Bar $delay="0s" />
          <Bar $delay="0.22s" />
          <Bar $delay="0.44s" />
          <Bar $delay="0.18s" />
          <Bar $delay="0.36s" />
        </BarsPanel>
        <GaugePanel>
          <GaugeArc />
          <GaugeNeedle />
          <GaugeDot />
        </GaugePanel>
      </LowerGrid>
    </DashboardScene>
  );
}

export default memo(AboutWorkVisual);

const motionSafe = css`
  animation-play-state: var(--motion-play-state, running);

  @media (prefers-reduced-motion: reduce) {
    animation: none !important;
    transition: none !important;
  }
`;

const Frame = styled.div`
  --accent-rgb: 130, 216, 244;
  --accent-bright-rgb: 228, 247, 255;
  --accent-deep-rgb: 88, 170, 208;
  --motion-play-state: ${(p) => (p.$motionActive ? "running" : "paused")};
  container-type: inline-size;
  content-visibility: auto;
  contain: layout paint style;
  position: relative;
  width: 100%;
  aspect-ratio: ${(p) => p.$aspect};
  border-radius: 28px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background:
    radial-gradient(circle at 14% 8%, rgba(var(--accent-rgb), 0.14), rgba(var(--accent-rgb), 0) 32%),
    radial-gradient(circle at 88% 10%, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0) 28%),
    linear-gradient(160deg, rgba(17, 20, 24, 0.98), rgba(7, 8, 10, 0.98));
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.06),
    0 26px 80px rgba(0, 0, 0, 0.28);
`;

const Backdrop = styled.div`
  position: absolute;
  inset: 0;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0)),
    radial-gradient(circle at 50% 50%, rgba(var(--accent-rgb), 0.05), rgba(var(--accent-rgb), 0) 46%);
`;

const Grid = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.18;
  background:
    repeating-linear-gradient(
      0deg,
      rgba(255, 255, 255, 0.05) 0,
      rgba(255, 255, 255, 0.05) 1px,
      transparent 1px,
      transparent 36px
    ),
    repeating-linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.05) 0,
      rgba(255, 255, 255, 0.05) 1px,
      transparent 1px,
      transparent 36px
    );
`;

const Glow = styled.div`
  position: absolute;
  inset: auto auto 10% -6%;
  width: 58%;
  height: 52%;
  background: radial-gradient(circle, rgba(var(--accent-rgb), 0.16), rgba(var(--accent-rgb), 0) 64%);
  opacity: 0.8;
  pointer-events: none;
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(180deg, rgba(6, 6, 6, 0.02), rgba(6, 6, 6, 0.12) 56%, rgba(6, 6, 6, 0.28));
`;

const AgentsScene = styled.div`
  position: absolute;
  inset: 0;
`;

const Phone = styled.div`
  position: absolute;
  left: 7%;
  top: 13%;
  width: 29%;
  height: 72%;
  border-radius: 32px;
  z-index: 1;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background:
    linear-gradient(180deg, rgba(16, 20, 24, 0.98), rgba(10, 13, 15, 0.96)),
    rgba(255, 255, 255, 0.03);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.06),
    0 20px 40px rgba(0, 0, 0, 0.28);
`;

const PhoneTop = styled.div`
  position: absolute;
  top: 12px;
  left: 50%;
  width: 34%;
  height: 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  transform: translateX(-50%);
`;

const PhoneBody = styled.div`
  position: absolute;
  inset: clamp(34px, 5.4cqw, 44px) clamp(10px, 2cqw, 16px) clamp(14px, 2.2cqw, 18px);
  display: grid;
  grid-template-rows: 0.74fr 1.34fr 0.74fr 0.64fr;
  gap: clamp(8px, 1.4cqw, 14px);
  align-items: stretch;
  min-height: 0;
`;

const ChatBubble = styled.div`
  height: 100%;
  width: ${(p) => p.$w || "64%"};
  min-height: 0;
  border-radius: clamp(12px, 2.4cqw, 18px);
  background: ${(p) =>
    p.$tone === "bright"
      ? "linear-gradient(135deg, rgba(var(--accent-bright-rgb), 0.9), rgba(var(--accent-rgb), 0.68))"
      : "rgba(255, 255, 255, 0.08)"};
  box-shadow: ${(p) =>
    p.$tone === "bright" ? "0 0 28px rgba(var(--accent-rgb), 0.22)" : "none"};

  &:nth-child(2n) {
    justify-self: end;
    align-self: flex-end;
  }
`;

const VoiceCallCard = styled.div`
  min-width: 0;
  min-height: 0;
  border-radius: clamp(18px, 4cqw, 28px);
  background:
    radial-gradient(circle at 24% 26%, rgba(var(--accent-bright-rgb), 0.9), rgba(var(--accent-rgb), 0.34) 40%, rgba(var(--accent-rgb), 0) 68%),
    linear-gradient(145deg, rgba(var(--accent-bright-rgb), 0.78), rgba(var(--accent-rgb), 0.58));
  box-shadow:
    0 0 30px rgba(var(--accent-rgb), 0.24),
    inset 0 1px 0 rgba(255, 255, 255, 0.22);
  display: grid;
  grid-template-columns: minmax(30px, 0.82fr) minmax(36px, 1fr);
  align-items: center;
  gap: clamp(5px, 1.2cqw, 10px);
  padding: clamp(7px, 1.45cqw, 12px);
  justify-self: end;
  width: 72%;
  overflow: hidden;
`;

const CallIconBadge = styled.span`
  width: clamp(26px, 5.6cqw, 44px);
  height: clamp(26px, 5.6cqw, 44px);
  border-radius: clamp(9px, 2cqw, 14px);
  display: inline-grid;
  place-items: center;
  justify-self: center;
  background:
    linear-gradient(145deg, rgba(6, 16, 20, 0.84), rgba(12, 30, 36, 0.68)),
    rgba(0, 0, 0, 0.28);
  border: 1px solid rgba(255, 255, 255, 0.18);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.14);
`;

const CallIcon = styled.svg`
  width: 64%;
  height: 64%;
  fill: rgba(var(--accent-bright-rgb), 0.96);
`;

const CallWaveStack = styled.div`
  height: 68%;
  display: grid;
  grid-template-columns: repeat(4, minmax(3px, 1fr));
  align-items: center;
  gap: clamp(3px, 0.7cqw, 6px);
`;

const CallWave = styled.span`
  display: block;
  width: clamp(5px, 1.2cqw, 8px);
  height: 100%;
  justify-self: center;
  border-radius: 999px;
  transform-origin: center;
  background: rgba(8, 18, 22, 0.42);
  animation: ${voiceLevel} 1.35s ease-in-out infinite;
  animation-delay: ${(p) => p.$delay || "0s"};
  ${motionSafe}
`;

const OpsPanel = styled.div`
  position: absolute;
  right: 6%;
  top: 14%;
  width: 49%;
  height: 71%;
  border-radius: 26px;
  z-index: 1;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background:
    linear-gradient(160deg, rgba(16, 20, 24, 0.94), rgba(11, 13, 16, 0.94)),
    rgba(255, 255, 255, 0.04);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.08),
    0 20px 42px rgba(0, 0, 0, 0.3);
  overflow: hidden;
`;

const OpsTop = styled.div`
  position: absolute;
  top: 11%;
  left: 9%;
  right: 9%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const OpsMark = styled.div`
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -0.04em;
  color: rgba(var(--accent-rgb), 0.96);
`;

const OpsBars = styled.div`
  display: flex;
  gap: 6px;
`;

const OpsBar = styled.div`
  width: 38px;
  height: 6px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);

  &:first-child {
    background: rgba(var(--accent-rgb), 0.86);
  }
`;

const OpsCard = styled.div`
  position: absolute;
  top: 32%;
  left: 9%;
  right: 9%;
  height: 22%;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.05);
  padding: 12px;
  display: grid;
  gap: 8px;
  min-height: 0;
  overflow: hidden;
`;

const OpsCardHeader = styled.div`
  width: 58%;
  height: 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
`;

const OpsRows = styled.div`
  display: grid;
  gap: 6px;
`;

const OpsRow = styled.div`
  width: ${(p) => p.$w};
  height: 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
`;

const OpsFooter = styled.div`
  position: absolute;
  left: 9%;
  right: 9%;
  bottom: 8%;
  height: 28%;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  align-items: stretch;
  min-height: 0;
`;

const MetricCard = styled.div`
  height: 100%;
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);
  padding: clamp(8px, 1.7cqw, 12px);
  display: grid;
  gap: 10px;
  align-content: start;
  min-height: 0;
  overflow: hidden;
`;

const MetricLine = styled.div`
  width: 54%;
  height: 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
`;

const MetricWave = styled.div`
  width: 100%;
  height: 28px;
  border-radius: 14px;
  background:
    linear-gradient(180deg, rgba(var(--accent-rgb), 0.18), rgba(var(--accent-rgb), 0)),
    linear-gradient(135deg, rgba(var(--accent-bright-rgb), 0.86), rgba(var(--accent-rgb), 0.26));
  clip-path: polygon(0% 88%, 10% 84%, 22% 70%, 36% 76%, 52% 40%, 66% 54%, 78% 30%, 100% 8%, 100% 100%, 0% 100%);
`;

const VoiceMeter = styled.div`
  width: 100%;
  height: clamp(24px, 5.2cqw, 40px);
  display: grid;
  grid-template-columns: repeat(5, minmax(4px, 1fr));
  gap: clamp(4px, 1cqw, 8px);
  align-items: center;
`;

const VoiceBar = styled.span`
  width: clamp(5px, 0.85cqw, 8px);
  height: 100%;
  justify-self: center;
  border-radius: 999px;
  transform-origin: center;
  background: linear-gradient(180deg, rgba(var(--accent-bright-rgb), 0.86), rgba(var(--accent-rgb), 0.34));
  box-shadow: 0 0 12px rgba(var(--accent-rgb), 0.18);
  animation: ${voiceLevel} 1.55s ease-in-out infinite;
  animation-delay: ${(p) => p.$delay || "0s"};
  ${motionSafe}

  &:nth-child(2),
  &:nth-child(5) {
    height: 76%;
  }

  &:nth-child(3) {
    height: 58%;
  }
`;

const IntegrationScene = styled.div`
  position: absolute;
  inset: 0;
`;

const Connections = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: visible;
`;

const ConnectionPath = styled.path`
  fill: none;
  stroke: rgba(var(--accent-rgb), 0.42);
  stroke-width: 0.42;
  stroke-linecap: round;
  stroke-linejoin: round;
`;

const FlowPath = styled.path`
  fill: none;
  stroke: rgba(var(--accent-bright-rgb), 0.82);
  stroke-width: 0.78;
  stroke-linecap: butt;
  stroke-linejoin: round;
  stroke-dasharray: 10 82;
  stroke-dashoffset: 0;
  opacity: 0.76;
  animation: ${flowBeam} 4.8s linear infinite;
  animation-delay: ${(p) => p.$delay || "0s"};
  ${motionSafe}
`;

const FlowStreak = styled.path`
  fill: none;
  stroke: rgba(var(--accent-bright-rgb), 0.96);
  stroke-width: 1.1;
  stroke-linecap: butt;
  stroke-linejoin: round;
  stroke-dasharray: 4 88;
  stroke-dashoffset: 0;
  opacity: 0.84;
  animation: ${flowBeam} 3.2s linear infinite;
  animation-delay: ${(p) => p.$delay || "0s"};
  ${motionSafe}
`;

const SystemNode = styled.div`
  position: absolute;
  top: ${(p) => p.$top};
  left: ${(p) => p.$left};
  width: ${(p) => p.$w};
  height: ${(p) => p.$h};
  border-radius: clamp(14px, 4cqw, 24px);
  border: 1px solid rgba(255, 255, 255, 0.16);
  background:
    radial-gradient(circle at 18% 18%, rgba(var(--accent-rgb), 0.1), rgba(var(--accent-rgb), 0) 42%),
    linear-gradient(160deg, rgba(18, 21, 24, 0.96), rgba(10, 12, 15, 0.96)),
    rgba(255, 255, 255, 0.04);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.06),
    0 18px 40px rgba(0, 0, 0, 0.26);
  padding: clamp(6px, 1.5cqw, 13px);
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  gap: clamp(4px, 1cqw, 10px);
  overflow: hidden;
`;

const SystemChrome = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
  position: relative;
  z-index: 1;
`;

const StatusLight = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: rgba(var(--accent-rgb), 0.95);
  box-shadow: 0 0 12px rgba(var(--accent-rgb), 0.42);
  animation: ${pulse} 2.8s ease-in-out infinite;
  ${motionSafe}
`;

const SystemLabel = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: ${(p) => (p.$tight ? "clamp(5px, 1.45cqw, 8px)" : "clamp(5px, 1.6cqw, 9px)")};
  font-weight: 800;
  letter-spacing: ${(p) => (p.$tight ? "0.04em" : "0.12em")};
  color: rgba(255, 255, 255, 0.58);

  @media (max-width: 560px) {
    letter-spacing: 0.04em;
  }
`;

const SyncCore = styled.div`
  position: absolute;
  left: 42%;
  top: 31%;
  width: 18%;
  height: 38%;
  border-radius: 26px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background:
    radial-gradient(circle at 50% 20%, rgba(var(--accent-rgb), 0.18), rgba(var(--accent-rgb), 0) 48%),
    linear-gradient(160deg, rgba(18, 21, 24, 0.98), rgba(10, 12, 15, 0.98)),
    rgba(255, 255, 255, 0.04);
  padding: clamp(9px, 1.5cqw, 13px) clamp(7px, 1.25cqw, 11px);
  display: grid;
  grid-template-rows: auto 1fr;
  gap: 8px;
  place-items: center;
  overflow: hidden;
  isolation: isolate;
  animation: ${coreBreathe} 4.2s ease-in-out infinite;
  ${motionSafe}
`;

const CoreOrbitSvg = styled.svg`
  position: absolute;
  inset: 8px;
  width: calc(100% - 16px);
  height: calc(100% - 16px);
  overflow: visible;
  pointer-events: none;
  z-index: 0;

  .core-track {
    fill: none;
    stroke: rgba(var(--accent-rgb), 0.18);
    stroke-width: 1.4;
  }

  .core-runner {
    fill: none;
    stroke: rgba(var(--accent-bright-rgb), 0.74);
    stroke-width: 2.7;
    stroke-linecap: round;
    stroke-dasharray: 18 82;
    stroke-dashoffset: 0;
    animation: ${orbitLoop} 5.2s linear infinite;
    ${motionSafe}
  }
`;

const CoreLabel = styled.strong`
  position: relative;
  z-index: 1;
  font-size: clamp(16px, 3.2cqw, 28px);
  line-height: 1;
  color: rgba(var(--accent-bright-rgb), 0.96);
`;

const CorePill = styled.span`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
  min-height: clamp(14px, 2.25cqw, 18px);
  max-width: 78%;
  padding: 0 clamp(5px, 0.9cqw, 8px);
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(var(--accent-rgb), 0.14);
  border: 1px solid rgba(var(--accent-rgb), 0.22);
  font-size: clamp(5px, 1.05cqw, 7px);
  font-weight: 900;
  letter-spacing: 0.1em;
  color: rgba(var(--accent-bright-rgb), 0.9);
`;

const CoreRows = styled.div`
  position: relative;
  z-index: 1;
  width: 74%;
  display: grid;
  gap: 5px;
  align-self: end;
`;

const CoreRow = styled.span`
  height: 5px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);

  &:nth-child(2) {
    width: 74%;
  }

  &:nth-child(3) {
    width: 88%;
  }
`;

const MiniCrm = styled.div`
  min-height: 0;
  display: grid;
  grid-template-columns: 34px 1fr;
  gap: 10px;
  align-items: center;
`;

const MiniAvatar = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background:
    radial-gradient(circle at 50% 35%, rgba(var(--accent-bright-rgb), 0.9), rgba(var(--accent-rgb), 0.34) 56%, rgba(var(--accent-rgb), 0.08) 57%),
    rgba(255, 255, 255, 0.06);
`;

const MiniCrmRows = styled.div`
  display: grid;
  gap: 7px;
`;

const MiniRow = styled.span`
  width: ${(p) => p.$w || "70%"};
  height: 7px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
`;

const MiniSvg = styled.svg`
  width: 100%;
  height: 100%;
  min-height: 0;
  display: block;
  overflow: visible;

  .mini-line {
    fill: rgba(255, 255, 255, 0.11);
  }

  .mini-panel {
    fill: rgba(var(--accent-bright-rgb), 0.12);
    stroke: rgba(var(--accent-bright-rgb), 0.18);
    stroke-width: 1;
  }

  .shop-price {
    fill: rgba(var(--accent-bright-rgb), 0.68);
    filter: drop-shadow(0 0 6px rgba(var(--accent-rgb), 0.18));
  }

  .invoice-check {
    fill: none;
    stroke: rgba(var(--accent-bright-rgb), 0.78);
    stroke-width: 5;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-dasharray: 54;
    animation: ${drawLine} 4.4s ease-in-out infinite;
    ${motionSafe}
  }

  .mini-area {
    fill: rgba(var(--accent-rgb), 0.18);
  }

  .mini-spark-base {
    fill: none;
    stroke: rgba(var(--accent-bright-rgb), 0.38);
    stroke-width: 4;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .mini-spark {
    fill: none;
    stroke: rgba(var(--accent-bright-rgb), 0.82);
    stroke-width: 4;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-dasharray: 150;
    animation: ${drawLine} 4.8s ease-in-out infinite;
    ${motionSafe}
  }
`;

const DashboardScene = styled.div`
  position: absolute;
  inset: 0;
  padding: clamp(14px, 4.1cqw, 20px);
  display: grid;
  grid-template-rows:
    minmax(clamp(52px, 15.2cqw, 74px), 0.9fr)
    minmax(clamp(48px, 13cqw, 64px), 0.62fr)
    minmax(clamp(66px, 18cqw, 88px), 0.98fr);
  gap: clamp(10px, 3.2cqw, 16px);
  min-height: 0;
`;

const DashboardTop = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: clamp(8px, 2.45cqw, 12px);
  min-height: 0;
`;

const KpiCard = styled.div`
  border-radius: clamp(14px, 3.3cqw, 16px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.052), rgba(255, 255, 255, 0.02)),
    rgba(255, 255, 255, 0.026);
  padding: clamp(8px, 2.45cqw, 12px);
  display: grid;
  grid-template-rows: auto auto minmax(0, 1fr);
  gap: clamp(5px, 1.45cqw, 7px);
  min-height: 0;
  overflow: hidden;
`;

const KpiHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
`;

const KpiLabel = styled.div`
  width: 46%;
  height: clamp(6px, 1.65cqw, 8px);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
`;

const KpiPulseBars = styled.div`
  display: inline-flex;
  align-items: end;
  gap: clamp(2px, 0.62cqw, 3px);
  height: clamp(10px, 2.85cqw, 14px);
  padding: 0 2px;
  opacity: 0.82;

  span {
    width: clamp(2px, 0.62cqw, 3px);
    height: 100%;
    border-radius: 999px;
    background: rgba(var(--accent-rgb), 0.72);
  }

  span:nth-child(2) {
    height: 76%;
  }

  span:nth-child(3) {
    height: 58%;
  }
`;

const KpiValueRow = styled.div`
  display: flex;
  align-items: center;
  gap: clamp(5px, 1.65cqw, 8px);
  min-width: 0;
`;

const KpiValue = styled.div`
  width: 58%;
  height: clamp(13px, 3.7cqw, 18px);
  border-radius: 999px;
  background: linear-gradient(90deg, rgba(var(--accent-bright-rgb), 0.96), rgba(var(--accent-rgb), 0.82));
`;

const KpiValueGhost = styled.div`
  flex: 1 1 auto;
  height: clamp(7px, 2.1cqw, 10px);
  min-width: 18px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.07);
`;

const KpiSparkSvg = styled.svg`
  width: 100%;
  height: 100%;
  min-height: 0;
  align-self: stretch;
  overflow: visible;

  .kpi-spark-area {
    fill: rgba(var(--accent-rgb), 0.21);
  }

  .kpi-spark-base,
  .kpi-spark-static {
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .kpi-spark-base {
    stroke: rgba(var(--accent-bright-rgb), 0.3);
    stroke-width: 3.5;
  }

  .kpi-spark-static {
    stroke: rgba(var(--accent-bright-rgb), 0.92);
    stroke-width: 3.5;
  }
`;

const ChartArea = styled.div`
  position: relative;
  min-height: 0;
  border-radius: clamp(14px, 3.7cqw, 18px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0)),
    rgba(255, 255, 255, 0.02);
  overflow: hidden;
`;

const MainChartSvg = styled.svg`
  position: absolute;
  inset: 12% 7% 13%;
  width: 86%;
  height: 75%;
  overflow: visible;

  .dashboard-main-area {
    fill: rgba(var(--accent-rgb), 0.28);
  }

  .dashboard-main-base,
  .dashboard-main-spark {
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .dashboard-main-base {
    stroke: rgba(var(--accent-bright-rgb), 0.28);
    stroke-width: 4.5;
  }

  .dashboard-main-spark {
    stroke: rgba(var(--accent-bright-rgb), 0.9);
    stroke-width: 4.5;
    stroke-dasharray: 150;
    animation: ${drawLine} 5.4s ease-in-out infinite;
    ${motionSafe}
  }
`;

const LowerGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr);
  gap: clamp(10px, 2.85cqw, 14px);
  min-height: 0;
`;

const BarsPanel = styled.div`
  min-height: 0;
  border-radius: clamp(14px, 3.7cqw, 18px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.03);
  padding: clamp(10px, 2.85cqw, 14px);
  display: flex;
  align-items: end;
  gap: clamp(7px, 2cqw, 10px);
`;

const Bar = styled.div`
  flex: 1 1 0;
  min-height: 30%;
  height: 100%;
  border-radius: 12px 12px 6px 6px;
  transform-origin: bottom;
  background: linear-gradient(180deg, rgba(var(--accent-bright-rgb), 0.96), rgba(var(--accent-rgb), 0.52));
  animation: ${riseBars} 3.6s ease-in-out infinite;
  animation-delay: ${(p) => p.$delay || "0s"};
  will-change: transform;
  ${motionSafe}
`;

const GaugePanel = styled.div`
  position: relative;
  min-height: 0;
  border-radius: clamp(14px, 3.7cqw, 18px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.03);
  overflow: hidden;
`;

const GaugeArc = styled.div`
  position: absolute;
  left: 50%;
  bottom: clamp(14px, 4.5cqw, 22px);
  width: clamp(70px, 19.2cqw, 94px);
  height: clamp(38px, 10.25cqw, 50px);
  border: clamp(6px, 1.65cqw, 8px) solid rgba(var(--accent-rgb), 0.4);
  border-bottom: 0;
  border-radius: 94px 94px 0 0;
  transform: translateX(-50%);
`;

const GaugeNeedle = styled.div`
  position: absolute;
  left: 50%;
  bottom: clamp(26px, 7.78cqw, 38px);
  width: clamp(36px, 9.4cqw, 46px);
  height: clamp(3px, 0.82cqw, 4px);
  border-radius: 999px;
  background: rgba(var(--accent-bright-rgb), 0.96);
  transform-origin: 4px 50%;
  transform: rotate(-30deg);
  animation: ${gaugeSwing} 4.2s ease-in-out infinite;
  ${motionSafe}
`;

const GaugeDot = styled.div`
  position: absolute;
  left: 50%;
  bottom: clamp(22px, 6.75cqw, 33px);
  width: clamp(10px, 2.45cqw, 12px);
  height: clamp(10px, 2.45cqw, 12px);
  border-radius: 50%;
  background: rgba(var(--accent-bright-rgb), 0.98);
  transform: translateX(-50%);
`;
